import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { useAuth } from '@/hooks/useAuth';
import { AppHeader, VersionChecker } from '@/components/common';
import { 
  WelcomeScreen, 
  LobbyScreen, 
  JoinHuntFlow,
  CreateHuntFlow,
  AuthScreen,
  HiderPage,
  HunterPage,
  SpectatorPage,
  TermsPage,
  PrivacyPage,
  MyHuntsPage,
} from '@/pages';
import StatsPage from '@/pages/StatsPage';

// Auth wrapper - initializes Firebase auth listener
const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  useAuth(); // This sets up the auth state listener
  return <>{children}</>;
};

// Standalone auth page wrapper with navigation
const AuthPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = (location.state as { returnTo?: string })?.returnTo || '/welcome';
  
  return <AuthScreen onComplete={() => navigate(returnTo, { replace: true })} />;
};

// Protected route wrapper
interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user, isLoading } = useAuthStore();

  if (isLoading) {
    return (
      <div className="h-dvh flex items-center justify-center">
        <div className="loading-spinner" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" state={{ returnTo: window.location.pathname }} replace />;
  }

  return (
    <div className="h-dvh flex flex-col overflow-hidden">
      <AppHeader />
      <main className="flex-1 overflow-hidden">
        {children}
      </main>
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <div className="min-h-screen text-text-primary">
        <VersionChecker />
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<Navigate to="/auth" replace />} />
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          
          {/* Welcome screen (after auth) */}
          <Route path="/welcome" element={
            <ProtectedRoute>
              <WelcomeScreen />
            </ProtectedRoute>
          } />
          
          {/* Join/Create flows (protected) */}
          <Route path="/join" element={
            <ProtectedRoute>
              <JoinHuntFlow />
            </ProtectedRoute>
          } />
          <Route path="/create" element={
            <ProtectedRoute>
              <CreateHuntFlow />
            </ProtectedRoute>
          } />
          <Route path="/my-hunts" element={
            <ProtectedRoute>
              <MyHuntsPage />
            </ProtectedRoute>
          } />
          
          {/* Protected routes */}
          <Route
            path="/lobby/:huntId"
            element={
              <ProtectedRoute>
                <LobbyScreen />
              </ProtectedRoute>
            }
          />
          <Route
            path="/hider/:huntId"
            element={
              <ProtectedRoute>
                <HiderPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/hunter/:huntId"
            element={
              <ProtectedRoute>
                <HunterPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/spectator/:huntId"
            element={
              <ProtectedRoute>
                <SpectatorPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/stats/:huntId"
            element={
              <ProtectedRoute>
                <StatsPage />
              </ProtectedRoute>
            }
          />
          
          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </AuthProvider>
  );
}

export default App;
